import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../components/Loading';
import PaginationBar from '../../../components/PaginationBar/PaginationBar';
import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import { searchRoutingSchedules } from '../../../services/routing/routingScheduleApi';

import { EmptyState } from '../../../styles/feedback/EmptyState';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
   SectionActions,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

import ScheduleRow from './components/ScheduleRow';
import { Breadcrumb, SortControl, List } from './styles';

const ITEMS_PER_PAGE = 20;

// Ordenação default do backend (handoff 0036 §2.3): sort_by=schedule_date DESC.
const DEFAULT_SORT_BY = 'schedule_date';
const DEFAULT_SORT_DIR = 'desc';
// Whitelist de sort_by — espelha EXATAMENTE o backend (regra 1.10 navegacao.md).
const SORTABLE_KEYS = new Set([
   'schedule_date',
   'total_unrouted',
   'total_items',
   'total_order_value',
   'updated_at',
]);
const SORT_OPTIONS = [
   { key: 'schedule_date', label: 'Data' },
   { key: 'total_unrouted', label: 'Pendentes' },
   { key: 'total_items', label: 'Total de itens' },
   { key: 'total_order_value', label: 'Valor total' },
   { key: 'updated_at', label: 'Atualizado em' },
];

// Chaves snake_case = nomes dos query params da URL = nomes do backend (1.1).
// "" = Todas/Todos (omitido no service, §1.4).
const DEFAULT_FILTERS = {
   cluster_origin: '',
   schedule_period: '',
   date_from: '',
   date_to: '',
   status: '',
};

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

function readSortFromUrl(searchParams) {
   const sortBy = searchParams.get('sort_by');
   if (sortBy && SORTABLE_KEYS.has(sortBy)) {
      return {
         sortBy,
         sortDir:
            searchParams.get('sort_direction') === 'asc' ? 'asc' : 'desc',
      };
   }
   return { sortBy: DEFAULT_SORT_BY, sortDir: DEFAULT_SORT_DIR };
}

function isDefaultSort(sort) {
   return sort.sortBy === DEFAULT_SORT_BY && sort.sortDir === DEFAULT_SORT_DIR;
}

// snake_case (URL/estado) → camelCase (args do service).
function toServiceArgs(filters, sort, page) {
   return {
      page,
      perPage: ITEMS_PER_PAGE,
      clusterOrigin: filters.cluster_origin,
      schedulePeriod: filters.schedule_period,
      dateFrom: filters.date_from,
      dateTo: filters.date_to,
      status: filters.status,
      sortBy: sort.sortBy,
      sortDirection: sort.sortDir,
   };
}

export default function RoutingScheduleList() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   // CTA placeholder → criação de item (schedule nasce on-demand, §5).
   const canCreate = hasPermission('routing:schedule_item:create');

   const [searchParams, setSearchParams] = useSearchParams();
   // URL = fonte da verdade: com query params (deep-link / F5 / voltar-do-board)
   // restauramos e buscamos no mount; URL limpa = cold-open (não busca). Leitura
   // única. `applied` distingue cold-open de "buscou e veio vazio".
   const hasInitialQueryParams = searchParams.toString() !== '';

   const [filters, setFilters] = useState(() =>
      readFiltersFromUrl(searchParams)
   );
   const [appliedFilters, setAppliedFilters] = useState(filters);
   const [sortConfig, setSortConfig] = useState(() =>
      readSortFromUrl(searchParams)
   );
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });
   const [applied, setApplied] = useState(hasInitialQueryParams);

   const [schedules, setSchedules] = useState([]);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);

   const didInitialFetch = useRef(false);

   // `applied=1` é o sentinela (1.5): pesquisar SEM filtros não gera param e a URL
   // ficaria limpa (indistinguível do cold-open). Com ele, voltar do board
   // re-hidrata a lista mesmo numa busca "todas as roteirizações".
   const commitToUrl = useCallback(
      (values, sort, page) => {
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         if (!isDefaultSort(sort)) {
            params.sort_by = sort.sortBy;
            params.sort_direction = sort.sortDir;
         }
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   const fetchSchedules = useCallback(async (values, sort, page) => {
      setIsLoading(true);
      try {
         const result = await searchRoutingSchedules(
            toServiceArgs(values, sort, page)
         );
         setSchedules(result.schedules);
         setTotalPages(result.totalPages);
         setTotalCount(result.total);
         setCurrentPage(result.page);
      } catch (err) {
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao buscar roteirizações.';
         toast.error(apiMessage);
      } finally {
         setIsLoading(false);
      }
   }, []);

   // Busca inicial só quando a URL traz filtros (deep-link / F5 / voltar). URL
   // limpa = cold-open, não dispara busca (§4).
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchSchedules(appliedFilters, sortConfig, currentPage);
   }, [
      fetchSchedules,
      hasInitialQueryParams,
      appliedFilters,
      sortConfig,
      currentPage,
   ]);

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   // Aplicar (submit / Enter) — busca explícita. Zera a ordenação pro default
   // (1.9) e volta à página 1.
   const handleSearch = (event) => {
      event.preventDefault();

      if (
         filters.date_from &&
         filters.date_to &&
         filters.date_from > filters.date_to
      ) {
         toast.error('A data inicial não pode ser maior que a data final.');
         return;
      }

      const resetSort = {
         sortBy: DEFAULT_SORT_BY,
         sortDir: DEFAULT_SORT_DIR,
      };
      setApplied(true);
      setAppliedFilters(filters);
      setSortConfig(resetSort);
      commitToUrl(filters, resetSort, 1);
      fetchSchedules(filters, resetSort, 1);
   };

   // Limpar — reseta tudo e volta ao cold-open, SEM buscar (§4).
   const handleClear = () => {
      const resetSort = {
         sortBy: DEFAULT_SORT_BY,
         sortDir: DEFAULT_SORT_DIR,
      };
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setSortConfig(resetSort);
      setSchedules([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      setApplied(false);
      setSearchParams({}, { replace: true });
   };

   // Ordenação é ação explícita → refaz a busca na hora com os filtros aplicados
   // e volta pra página 1. Usa appliedFilters (não o rascunho).
   const applySort = (nextSort) => {
      setSortConfig(nextSort);
      commitToUrl(appliedFilters, nextSort, 1);
      fetchSchedules(appliedFilters, nextSort, 1);
   };

   const handleSortByChange = (value) => {
      if (!SORTABLE_KEYS.has(value)) return;
      applySort({ sortBy: value, sortDir: sortConfig.sortDir });
   };

   const handleToggleDir = () => {
      applySort({
         sortBy: sortConfig.sortBy,
         sortDir: sortConfig.sortDir === 'asc' ? 'desc' : 'asc',
      });
   };

   const handlePageChange = (page) => {
      commitToUrl(appliedFilters, sortConfig, page);
      fetchSchedules(appliedFilters, sortConfig, page);
   };

   const handleOpen = (schedule) => {
      const base = schedule.cluster_origin?.code ?? '';
      navigate(
         `/routing/management?date=${schedule.schedule_date}` +
            `&period=${schedule.schedule_period}&base=${base}`
      );
   };

   const hasResults = schedules.length > 0;
   // Pendentes do header = soma das linhas visíveis (handoff 0036 §3).
   const pendingSum = schedules.reduce(
      (acc, s) => acc + (Number(s.total_unrouted) || 0),
      0
   );

   let subtitle;
   if (!applied) {
      subtitle = 'Use os filtros e pesquise para listar as roteirizações.';
   } else {
      const noun = totalCount === 1 ? 'roteirização' : 'roteirizações';
      subtitle = `${totalCount} ${noun} · ${pendingSum} pendentes`;
   }

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <Breadcrumb aria-label="Caminho">
            <span>Roteirização</span>
            <span className="sep" aria-hidden="true">
               ›
            </span>
            <span className="current">Roteirizações</span>
         </Breadcrumb>

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Roteirizações</PageTitle>
               <PageSubtitle>{subtitle}</PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canCreate && (
                  <button
                     type="button"
                     onClick={() => navigate('/routing/items/new')}
                  >
                     + Nova roteirização
                  </button>
               )}
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Filtros (5 campos, sem "Buscar" — §1.1) ────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-3">
                        <label htmlFor="filter-base">Base</label>
                        <select
                           id="filter-base"
                           value={filters.cluster_origin}
                           onChange={(e) =>
                              updateField('cluster_origin', e.target.value)
                           }
                        >
                           <option value="">Todas as bases</option>
                           <option value="GOIANIA">Goiânia</option>
                           <option value="ANAPOLIS">Anápolis</option>
                        </select>
                     </Field>

                     <Field className="col-2">
                        <label htmlFor="filter-period">Período</label>
                        <select
                           id="filter-period"
                           value={filters.schedule_period}
                           onChange={(e) =>
                              updateField('schedule_period', e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           <option value="MANHA">Manhã</option>
                           <option value="TARDE">Tarde</option>
                        </select>
                     </Field>

                     <Field className="col-2">
                        <label htmlFor="filter-date-from">Data de</label>
                        <input
                           id="filter-date-from"
                           type="date"
                           value={filters.date_from}
                           onChange={(e) =>
                              updateField('date_from', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-2">
                        <label htmlFor="filter-date-to">Data até</label>
                        <input
                           id="filter-date-to"
                           type="date"
                           value={filters.date_to}
                           onChange={(e) =>
                              updateField('date_to', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="filter-status">Status</label>
                        <select
                           id="filter-status"
                           value={filters.status}
                           onChange={(e) =>
                              updateField('status', e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           <option value="pending">Rascunho</option>
                           <option value="special">Em preparação</option>
                           <option value="progress">Em execução</option>
                           <option value="done">Concluída</option>
                        </select>
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <button
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </button>
                        <button type="submit" disabled={isLoading}>
                           Pesquisar
                        </button>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ─────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Resultados</SectionTitle>
               {applied && hasResults && (
                  <SectionActions>
                     <SortControl>
                        <span>Ordenar</span>
                        <select
                           aria-label="Ordenar por"
                           value={sortConfig.sortBy}
                           onChange={(e) => handleSortByChange(e.target.value)}
                        >
                           {SORT_OPTIONS.map((opt) => (
                              <option key={opt.key} value={opt.key}>
                                 {opt.label}
                              </option>
                           ))}
                        </select>
                        <button
                           type="button"
                           onClick={handleToggleDir}
                           title={
                              sortConfig.sortDir === 'asc'
                                 ? 'Crescente'
                                 : 'Decrescente'
                           }
                           aria-label="Inverter direção da ordenação"
                        >
                           {sortConfig.sortDir === 'asc' ? '▲' : '▼'}
                        </button>
                     </SortControl>
                  </SectionActions>
               )}
            </SectionHeader>
            <SectionBody>
               {!applied && (
                  <EmptyState
                     title="Nenhuma roteirização carregada"
                     description="Selecione base, período ou data e clique em Aplicar para listar as roteirizações existentes."
                  />
               )}

               {applied && !hasResults && (
                  <EmptyState
                     title="Nenhum resultado"
                     description="Nenhuma roteirização corresponde aos filtros. Ajuste os critérios e tente novamente."
                  />
               )}

               {applied && hasResults && (
                  <>
                     <List>
                        {schedules.map((schedule) => (
                           <ScheduleRow
                              key={schedule.id}
                              schedule={schedule}
                              onOpen={() => handleOpen(schedule)}
                           />
                        ))}
                     </List>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
